import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

import { CALLBACK_PATH } from '@/lib/auth/oidcConfig';
import { isSafeReturnTo } from '@/lib/auth/returnTo';

/**
 * Sends an unauthenticated visitor to the identity provider, and brings them back
 * to the page they asked for (spec 067 FR-003, SC-004).
 *
 * A component rather than a router `beforeLoad` guard, deliberately. `beforeLoad`
 * throwing `redirect()` is the right tool for moving between routes of this app;
 * signing in is a *full page navigation to another origin*, which the router has no
 * say over. Doing it here also means the "still deciding" state — the moment before
 * the library has restored a session — is renderable, and does not flash the
 * sign-in redirect at someone who is already signed in.
 *
 * The requested URL is carried in the OIDC `state` and restored by the callback
 * route, so a deep link survives the round trip.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const onCallback = window.location.pathname === CALLBACK_PATH;

  // `react-oidc-context` hands back a new `auth` object on every internal tick
  // (silent renew, `activeNavigator` change — see SessionProvider.tsx's own
  // comment on this), and React StrictMode double-invokes a mount effect. Either
  // one re-runs the effect below before `auth.activeNavigator` has had a chance
  // to become truthy and block it, so without a guard `signinRedirect` can fire
  // twice for one mount — and the second call's `returnTo`, read from
  // `window.location` at that later tick, is the one that wins the race to the
  // identity provider. This ref makes "send to sign-in" happen at most once per
  // mount, decided from the location at the moment of that one decision,
  // independent of how many times the effect itself re-runs afterward.
  const hasRedirectedRef = useRef(false);
  const returnToRef = useRef<string | null>(null);
  if (returnToRef.current === null) {
    const requested = window.location.pathname + window.location.search;
    // `pathname` is always same-document, but validate anyway rather than trust
    // that invariant forever — this is the value that round-trips through the
    // OIDC `state` and comes back out the other side in AuthCallback.tsx.
    returnToRef.current = isSafeReturnTo(requested) ? requested : '/';
  }

  useEffect(() => {
    if (
      onCallback ||
      auth.isLoading ||
      auth.isAuthenticated ||
      auth.activeNavigator ||
      // Without this the error branch below renders and this effect immediately
      // sends the browser back to the provider that just failed — a loop against a
      // misconfigured `VITE_OIDC_AUTHORITY` or a briefly unreachable IdP, in which
      // the message explaining what went wrong is never on screen long enough to
      // read. A failed sign-in is a stop, not a retry.
      auth.error ||
      hasRedirectedRef.current
    ) {
      return;
    }
    hasRedirectedRef.current = true;
    void auth.signinRedirect({
      state: { returnTo: returnToRef.current },
    });
  }, [auth, onCallback]);

  // The callback route must render while signed out — it is where signing in
  // finishes. Guarding it would redirect the browser away mid-exchange, losing the
  // authorization code and looping.
  if (onCallback) return <>{children}</>;

  if (auth.error) {
    return (
      <div className="text-muted-foreground flex min-h-dvh items-center justify-center p-8 text-sm">
        <p role="alert">
          Could not reach the sign-in service. Refresh to try again.
        </p>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    // Intentionally bare: this is on screen for the moment before a redirect, and
    // a spinner that flashes for 80ms reads as jank rather than progress.
    return <div className="min-h-dvh" aria-busy="true" />;
  }

  return <>{children}</>;
}
